import React, { useState } from 'react';
import { WorksheetTask } from '../../types/worksheet';
import {
  CheckIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface TaskChecklistProps {
  tasks: WorksheetTask[];
  onChange: (tasks: WorksheetTask[]) => void;
  onToggle: (taskId: string) => void;
  readOnly?: boolean;
}

const TaskChecklist: React.FC<TaskChecklistProps> = ({ tasks, onChange, onToggle, readOnly = false }) => {
  const { t } = useLanguage();
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const addTask = () => {
    if (!newTaskTitle.trim()) return;
    const task: WorksheetTask = {
      id: `task_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      title: newTaskTitle.trim(),
      isCompleted: false,
      order: tasks.length,
    };
    onChange([...tasks, task]);
    setNewTaskTitle('');
  };

  const removeTask = (id: string) => {
    onChange(tasks.filter(t => t.id !== id));
  };

  const moveTask = (id: string, direction: 'up' | 'down') => {
    const index = tasks.findIndex(t => t.id === id);
    if (index < 0) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= tasks.length) return;
    const reordered = [...tasks];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    reordered.forEach((t, idx) => { t.order = idx; });
    onChange(reordered);
  };

  const completedCount = tasks.filter(t => t.isCompleted).length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700">
        <div className="flex items-center justify-between">
          <h3 className="text-xs sm:text-sm font-semibold text-white">{t('worksheets.tasks.title')}</h3>
          <span className="text-[10px] sm:text-xs text-white/80">{completedCount}/{tasks.length} {t('worksheets.tasks.completed')}</span>
        </div>
        {/* Progress bar */}
        {tasks.length > 0 && (
          <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all"
              style={{ width: `${(completedCount / tasks.length) * 100}%` }}
            />
          </div>
        )}
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {tasks.map((task) => (
          <div key={task.id} className="px-3 py-1.5 sm:px-4 sm:py-2 flex items-center space-x-2 sm:space-x-3 group hover:bg-gray-50 dark:hover:bg-gray-700/30">
            <button
              onClick={() => onToggle(task.id)}
              disabled={readOnly}
              className={`w-4 h-4 sm:w-5 sm:h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                task.isCompleted
                  ? 'border-green-500 bg-green-500'
                  : 'border-gray-300 dark:border-gray-600 hover:border-green-400'
              } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
            >
              {task.isCompleted && <CheckIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
            </button>
            <span className={`flex-1 text-xs sm:text-sm ${task.isCompleted ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {task.title}
            </span>
            {task.estimatedMinutes && (
              <span className="text-[10px] sm:text-xs text-gray-400">~{task.estimatedMinutes}p</span>
            )}
            {!readOnly && (
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => moveTask(task.id, 'up')} className="p-0.5 text-gray-400 hover:text-gray-600">
                  <ChevronUpIcon className="w-3 h-3" />
                </button>
                <button onClick={() => moveTask(task.id, 'down')} className="p-0.5 text-gray-400 hover:text-gray-600">
                  <ChevronDownIcon className="w-3 h-3" />
                </button>
                <button onClick={() => removeTask(task.id)} className="p-0.5 text-red-400 hover:text-red-600">
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="px-3 py-1.5 sm:px-4 sm:py-2 border-t border-gray-100 dark:border-gray-700 flex items-center space-x-2">
          <input
            type="text"
            value={newTaskTitle}
            onChange={e => setNewTaskTitle(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTask()}
            placeholder={t('worksheets.tasks.add') + '...'}
            className="flex-1 text-xs sm:text-sm bg-transparent border-0 focus:outline-none placeholder-gray-400 text-gray-700 dark:text-gray-300"
          />
          <button
            onClick={addTask}
            className="inline-flex items-center px-2 py-1 text-[10px] sm:text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
          >
            <PlusIcon className="w-3 h-3 mr-1" />
            {t('worksheets.actions.add')}
          </button>
        </div>
      )}
    </div>
  );
};

export default TaskChecklist;
